import React, { useState } from 'react';
import Modal from 'react-modal';
import styled from '@emotion/styled';
import { FaTimes } from 'react-icons/fa';
import { navigate } from '@reach/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { validate, downloadResource, validatePhoneNumber } from './hsAPI';
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Input,
  Link,
  Spinner,
} from '@theme-ui/components';

const ShowProcessing = () => (
  <Box sx={{ mx: 1 }}>
    <Spinner color="primary" size="24" />
  </Box>
);

const CloseBtn = ({ closeModal }) => (
  <Box sx={{ textAlign: 'right', mt: -2, mr: -3 }}>
    <Button sx={styledCloseBtn} onClick={closeModal}>
      <FaTimes style={{ color: '#867E7E' }} />
    </Button>
  </Box>
);

let recaptchaRef = React.createRef();

const HSDownloadForm = ({
  formCode,
  pageCode,
  pageName,
  pageUrl,
  resourceCardImg,
  resourceId,
  resourceType,
  closeModal,
  regionPath
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [websiteURL, setWebsiteURL] = useState('');
  const [company, setCompany] = useState('');

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [hasError, setHasError] = useState(false);
  const [message, setMessage] = useState('');

  const formValid = () => {
    if (name && email && phoneNumber) {
      return true;
    }
    return false;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setHasError(false);
    setProcessing(true);
    setMessage('');

    let error = validate(email);
    setError(error);
    if (error) {
      setProcessing(false);
      return;
    }
    error = validatePhoneNumber(phoneNumber);
    setError(error);
    if (error) {
      setProcessing(false);
      return;
    }
    if (recaptchaRef && recaptchaRef.current) {
      let captcha = '1';
      recaptchaRef.current.reset();
      captcha = await recaptchaRef.current.executeAsync();
      if (captcha) {
        const formData = {
          hs_name: name,
          hs_phone: phoneNumber,
          hs_email: email,
          hs_website: websiteURL,
          hs_company: company,
          invisible_recaptcha: true,
          hubspot_form_code: formCode,
          captcha,
          page_code: pageCode,
          page_name: pageName,
          page_url: pageUrl,
          resource_id: resourceId,
          resource_card_image_url: resourceCardImg?.url,
        }
        downloadResource(
          formData,
          (response) => {
            setProcessing(false);
            if (response.status === 200 && response.data['resource_url']) {
              const resourceUrl = response.data['resource_url'];
              navigate(regionPath?`/${regionPath}/downloadable-resource/thank-you/`:'/downloadable-resource/thank-you/', {
                state: {
                  resourceUrl: resourceUrl,
                  resourceName: pageName,
                  resourceCardImg: resourceCardImg,
                  resourceType: resourceType,
                  isEmailSent: true
                },
              });
            } else {
              setHasError(true);
            }
          },
          (error) => {
            setProcessing(false);
            if (
              error.response &&
              error.response.status == 400 &&
              error.response.data.invalid_email
            ) {
              setEmailError(error.response.data.invalid_email);
            } else {
              setHasError(true);
            }
           }
         );
      } else {
        setProcessing(false);
        setHasError(true);
      }
    } else {
      setProcessing(false);
      setHasError(true);
    }
  };

  return (
    <Box as="form" sx={{ fontFamily: 'assistant' }}>
      <CloseBtn closeModal={closeModal} />
      <Heading as="h1" sx={{ fontSize: 3, fontWeight: 'bold', mb: 3 }}>
        Download Now
      </Heading>
      <Grid gap={4} columns={[1, 1, 2, 3]}>
        <StyledInput
          type="text"
          placeholder="Name*"
          value={name}
          name="name"
          required
          maxLength="100"
          onChange={(e) => setName(e.target.value)}
        />
        <StyledInput
          type="email"
          placeholder="Email*"
          value={email}
          name="email"
          required
          maxLength="100"
          onChange={(e) => setEmail(e.target.value)}
        />
        <StyledInput
          type="text"
          placeholder="Phone Number*"
          value={phoneNumber}
          name="phoneNumber"
          required
          maxLength="20"
          onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
        />
        <StyledInput
          type="text"
          placeholder="Website URL"
          value={websiteURL}
          name="websiteUrl"
          maxLength="100"
          onChange={(e) => setWebsiteURL(e.target.value)}
        />
        <StyledInput
          type="text"
          placeholder="Company Name"
          value={company}
          name="company"
          maxLength="200"
          onChange={(e) => setCompany(e.target.value)}
        />
      </Grid>
      <Box sx={{ fontSize: '14px', maxWidth: 611, mt: 4 }}>
        We're committed to your privacy. greytHR uses the information you
        provide to us to contact you about our relevant content, products, and
        services. You may unsubscribe from these communications at any time. For
        more information, check out our{' '}
        <Link href="/privacy-policy/" target="_blank" sx={styledLink}>
          Privacy Policy
        </Link>
      </Box>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.GATSBY_INVISIBLE_RE_CAPTCHA_SITE_KEY}
        badge='bottomleft'
      />
      <Flex
        sx={{
          mt: 4,
          pb: [3, 3, 3, 1],
          alignItems: 'center',
          flexDirection: ['column', 'row'],
        }}>
        <Button
          sx={{ fontSize: '20px', '&: disabled': { opacity: 0.5 } }}
          onClick={onSubmit}
          disabled={!formValid()}>
          Download Now
        </Button>
        <Box sx={{ ml: [0, 5], my: [3, 0], color: 'orange2' }}>
          {processing ? <ShowProcessing /> : null}
          {error ? <b>{error}</b> : null}
          {hasError ? <b>Sorry, some error seems to have occurred!</b> : null}
          {message ? (
            <Box sx={{ color: 'primary', fontWeight: 'bold' }}>{message}</Box>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
};

const ResourceDownloadPopup = ({
  isModalOpen,
  setIsModalOpen,
  formCode,
  pageCode,
  pageName,
  pageUrl,
  resourceId,
  resourceCardImg,
  resourceType,
  regionPath
}) => {
  const closeModal = (event) => {
    event.preventDefault();
    setIsModalOpen(false);
  };
  return (
    <Modal
      isOpen={isModalOpen}
      contentLabel="EmailVerification"
      shouldCloseOnOverlayClick={false}
      onRequestClose={closeModal}
      ariaHideApp={false}
      style={modalStyle}>
      <HSDownloadForm
        resourceId={resourceId}
        resourceType={resourceType}
        resourceCardImg={resourceCardImg}
        formCode={formCode}
        pageCode={pageCode}
        pageName={pageName}
        pageUrl={pageUrl}
        closeModal={closeModal}
        regionPath={regionPath}
      />
    </Modal>
  );
};

const modalStyle = {
  content: {
    position: 'static',
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
    height: 420,
    maxWidth: 773,
    width: ['90%', '90%', '85%', 773],
    p: 3,
  },
  overlay: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
};

const styledCloseBtn = {
  bg: 'white',
  width: 10,
  p: 1,
  '&:hover': {
    bg: 'white',
    boxShadow: 'none',
  },
};

const StyledInput = styled(Input)`
  background: #ebebeb;
  border-radius: 4px;
  font-size: 16px;
  height: 48px;
  margin: 0px;
  ::placeholder {
    color: #383838;
    opacity: 1;
  }
`;
const styledLink = {
  color: 'primary',
  '&:hover, &:active': {
    color: 'primary',
  },
};

export default ResourceDownloadPopup;
